import classNames from 'classnames'
import dayjs from 'dayjs'
import React, {useContext, useMemo} from 'react'
import {Avatar} from 'antd'
import {BsDropletFill, BsHeartFill, BsPersonGear, BsPersonVcard, BsStar, BsStarFill} from 'react-icons/bs'
import {GoDotFill} from 'react-icons/go'
import {IoCallOutline, IoHomeOutline, IoLocationOutline, IoMailOutline} from 'react-icons/io5'
import {PiGearSix, PiUserCircleDashedFill, PiUsersThree, PiUsersThreeFill} from 'react-icons/pi'
import {FaFire} from 'react-icons/fa'
import {MdRingVolume} from 'react-icons/md'
import {Link} from 'react-router-dom'
import {organization_getMemberPersonalProfile_Api} from '../../../apis'
import {catholicHousehold_getMyCatholicHouseholds_Api} from '../../../apis/catholicHousehold'
import getLinkToDetail from '../../../helpers/getLinkToDetail'
import useAsync from '../../../modules/asyncCache/useAsync'
import useTranslate from '../../../modules/local/useTranslate'
import {getResponseItem, getResponseItems, NullAsDefault, renderIf} from '../../Shared'
import LoadingBlock from '../../Loading/LoadingBlock'
import WorkspaceContext from '../WorkspaceContext'
import {staticPaths} from '../../../routes/staticPaths'
import getAliasName from "../../../helpers/getAliasName";

// ─── Helpers ────────────────────────────────────────────────────────────────

const SACRAMENT_ORDER = [
    'baptism',
    'first_communion',
    'confirmation',
    'matrimony',
    'holy_orders',
]

const SACRAMENT_META = {
    baptism: {color: 'text-blue-500', bg: 'bg-blue-50', icon: <BsDropletFill size={13}/>, labelKey: 'baptism'},
    first_communion: {
        color: 'text-green-600',
        bg: 'bg-green-50',
        icon: <BsStarFill size={11}/>,
        labelKey: 'first communion'
    },
    confirmation: {color: 'text-violet-500', bg: 'bg-violet-50', icon: <FaFire size={12}/>, labelKey: 'confirmation'},
    matrimony: {color: 'text-rose-500', bg: 'bg-rose-50', icon: <BsHeartFill size={12}/>, labelKey: 'matrimony'},
    holy_orders: {color: 'text-amber-600', bg: 'bg-amber-50', icon: <MdRingVolume size={13}/>, labelKey: 'holy orders'},
}

function daysUntilNextAnniversary(dateStr) {
    if (!dateStr) return null
    const ref = dayjs(dateStr, ['YYYY-MM-DD', 'DD/MM/YYYY', 'YYYY-MM-DDTHH:mm:ssZ'])
    if (!ref.isValid()) return null
    const today = dayjs().startOf('day')
    let next = today.year(today.year()).month(ref.month()).date(ref.date())
    if (next.isBefore(today)) next = next.add(1, 'year')
    return next.diff(today, 'day')
}

function yearsSince(dateStr) {
    if (!dateStr) return null
    const d = dayjs(dateStr, ['YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ssZ'])
    if (!d.isValid()) return null
    return dayjs().diff(d, 'year')
}

function formatDisplayDate(dateStr) {
    if (!dateStr) return null
    const d = dayjs(dateStr, ['YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ssZ'])
    if (!d.isValid()) return dateStr
    return d.format('DD/MM/YYYY')
}

function formatMonthDay(dateStr) {
    if (!dateStr) return null
    const d = dayjs(dateStr, ['YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ssZ', 'DD/MM', 'MM/DD'])
    if (!d.isValid()) return dateStr
    return d.format('DD/MM')
}

function getInitials(name = '') {
    return name.trim().split(/\s+/).slice(-2).map(w => w[0]?.toUpperCase()).join('')
}

// ─── Section card wrapper ────────────────────────────────────────────────────

const SectionCard = ({title, icon, children, className = ''}) => (
    <div className={classNames('background border border-color-50 shadow-sm rounded-xl overflow-hidden', className)}>
        <div className="px-4 py-3 flex items-center gap-2 border-b border-color-50">
            {icon && <span className="flex flex-center text-secondary-300">{icon}</span>}
            <span className="font-semibold text-sm text-secondary-600 tracking-wide">{title}</span>
        </div>
        <div className="p-4">{children}</div>
    </div>
)

// ─── Date row ────────────────────────────────────────────────────────────────

const DateRow = ({icon, iconBg, iconColor, label, dateStr, showAnniversary = false, isBirthday = false}) => {
    const t = useTranslate()
    const days = showAnniversary ? daysUntilNextAnniversary(dateStr) : null
    const years = showAnniversary ? yearsSince(dateStr) : null
    const displayDate = dateStr
        ? dayjs(dateStr, ['YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ssZ']).isValid()
            ? formatDisplayDate(dateStr)
            : formatMonthDay(dateStr)
        : null

    if (!displayDate) return null

    return (
        <div className="flex items-center gap-3 py-2 border-b border-color-50 last:border-b-0">
            <div className={classNames('w-7 h-7 rounded-lg flex flex-center flex-shrink-0', iconBg)}>
                <span className={`flex flex-center ${iconColor}`}>
                    {icon}
                </span>
            </div>
            <span className="flex-1 font-medium text-sm text-color-100">{label}</span>
            <div className="flex items-center gap-2">
                <span className="font-semibold text-sm text-color-000">
                    {displayDate}
                </span>
                {years !== null && years > 0 && (
                    <span className="text-xs font-medium text-color-100 italic">
                        <GoDotFill size={7} className="text-color-500"/>
                        {` ${years} `}
                        <span className="font-normal text-color-400">{isBirthday ? t('years old') : t('years')}</span>
                    </span>
                )}
                {days !== null && days <= 30 && (
                    <span
                        className={classNames(
                            'text-xs font-bold px-2 py-0.5 rounded-full', days === 0 ? 'bg-rose-100 text-rose-600' : 'bg-amber-50 text-amber-600'
                        )}>
                        <GoDotFill size={7} className="text-color-500"/>
                        <span className="font-normal text-color-400"> {days === 0 ? t('to day') : `${days} ${t('days remaining')}`} </span>
                    </span>
                )}
                {days !== null && days > 30 && (
                    <span className="text-xs text-color-400 italic tracking-wider">
                        <GoDotFill size={7} className="text-color-500"/>
                        {` ${days} `}
                        <span className="font-normal text-color-400"> {t('days remaining')} </span>
                    </span>
                )}
            </div>
        </div>
    )
}

// ─── Main component ──────────────────────────────────────────────────────────

const WorkspaceMemberPersonalProfile = () => {
    const t = useTranslate()
    const {organization} = useContext(WorkspaceContext)

    const {response, isLoading} = useAsync({
        apiInfo: organization_getMemberPersonalProfile_Api,
        query: {':id': organization?.id},
    })

    const profile = getResponseItem(response)

    const {response: hhResponse} = useAsync({
        apiInfo: catholicHousehold_getMyCatholicHouseholds_Api,
        values: {organization_id: organization?.id},
    })
    const orgHouseholds = getResponseItems(hhResponse)

    const sortedSacraments = useMemo(() => {
        if (!profile?.sacraments) return []
        return [...profile.sacraments].sort(
            (a, b) => SACRAMENT_ORDER.indexOf(a.sacrament_type) - SACRAMENT_ORDER.indexOf(b.sacrament_type)
        )
    }, [profile?.sacraments])

    const matrimonySacrament = useMemo(
        () => profile?.sacraments?.find(s => s.sacrament_type === 'matrimony'),
        [profile?.sacraments]
    )

    if (!organization?.id) return null
    if (isLoading && !profile) return <LoadingBlock style={{minHeight: 300}}/>
    if (!profile) return null

    const initials = getInitials(profile.name || '')
    const genderLabel = profile.gender === 1 ? t('male') : profile.gender === 2 ? t('female') : NullAsDefault

    return (
        <div className="flex flex-col gap-4">
            {/* ── Profile header ── */}
            <div className="p-5 background border border-color-50 shadow-md rounded-xl">
                <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <Avatar
                        size={60}
                        src={profile?.avatar}
                        style={{ background: 'linear-gradient(to right, var(--color-primary), var(--color-primary-700))' }}
                        className="flex flex-center shadow-md flex-shrink-0">
                        <span className="font-bold text-white text-xl">
                            {!profile?.avatar && initials}
                        </span>
                    </Avatar>

                    {/* Name + meta */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex flex-col gap-1">
                                <h2 className="font-bold text-xl text-color-000 m-0 leading-tight">
                                    {renderIf(
                                        profile?.patron_saint_name,
                                        <span className="mr-1 font-medium text-gray-600">
                                            {profile?.patron_saint_name}
                                        </span>
                                    )}
                                    {profile?.name}
                                </h2>
                                <div className="flex items-center gap-3 flex-wrap">
                                    {profile?.identifier_code && (
                                        <span className="font-mono font-bold text-xs text-blue-500 tracking-wide">
                                          {'ID:'} {profile?.identifier_code}
                                        </span>
                                    )}
                                    {genderLabel !== NullAsDefault && (
                                        <span className="flex items-center gap-1 text-xs text-color-300 tracking-wider">
                                            <GoDotFill size={8}/>
                                            {genderLabel}
                                        </span>
                                    )}
                                    {profile?.date_of_birth && (
                                        <span className="flex items-center gap-1 text-xs text-color-300 tracking-wider">
                                            <GoDotFill size={8}/>
                                            {t('born')}: {formatDisplayDate(profile.date_of_birth)}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <Link
                                to={staticPaths.accountSettings.pathname}
                                style={{ padding: '0.25rem 0.5rem', borderRadius: '0.5rem', fontWeight: 600, textDecoration: 'none' }}
                                className="flex items-center gap-1.5 text-xs text-color-200 background-200 hover:bg-primary-50 transition-all">
                                <PiGearSix size={13}/>
                                {t('edit')}
                            </Link>
                        </div>

                        {/* Department membership tags */}
                        {profile.department_memberships?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {profile.department_memberships.map((dm, i) => (
                                    <span
                                        key={dm.id || i}
                                        className="px-2 py-0.5 text-xs font-medium border border-secondary-400 bg-secondary-50 text-secondary-600 rounded-full">
                                        {dm.role_title || dm.department_title}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── 2-column layout ── */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                {/* Left: anniversaries + system account */}
                <div className="lg:col-span-3 flex flex-col gap-4">
                    {/* Các ngày kỷ niệm */}
                    <SectionCard
                        icon={<BsStar size={14}/>}
                        title={t('anniversaries')}>
                        {/* Bí tích & Sự kiện Tôn giáo */}
                        <div className="mb-4 flex flex-col">
                            <p className="mb-2 text-xs font-semibold text-color-400 uppercase tracking-widest">
                                {t('sacraments & religious events')}
                            </p>
                            <div className="flex flex-col">
                                {sortedSacraments.map((s, i) => {
                                    const meta = SACRAMENT_META[s.sacrament_type] || {
                                        color: 'text-color-300', bg: 'bg-color-50',
                                        icon: <GoDotFill size={10}/>, labelKey: s.sacrament_type,
                                    }
                                    const isMarriage = s.sacrament_type === 'matrimony'
                                    return (
                                        <DateRow
                                            key={i}
                                            icon={meta.icon}
                                            iconBg={meta.bg}
                                            iconColor={meta.color}
                                            label={t(meta.labelKey)}
                                            dateStr={s.eff_date}
                                            showAnniversary={isMarriage}
                                        />
                                    )
                                })}

                                {/* Patron saint feast */}
                                {profile.patron_saint_name && profile.patron_saint_feast_day && (
                                    <DateRow
                                        icon={<BsStarFill size={11}/>}
                                        iconBg="bg-amber-50"
                                        iconColor="text-amber-500"
                                        label={`${t('patron saint')} (${profile.patron_saint_name})`}
                                        dateStr={profile.patron_saint_feast_day}
                                        showAnniversary={true}
                                    />
                                )}

                                {sortedSacraments.length === 0 && !profile.patron_saint_name && (
                                    <p className="p-2 background-100 rounded-lg text-color-400 italic">{t('no data')}</p>
                                )}
                            </div>
                        </div>
                        {/* Sinh nhật & Kỷ niệm gia đình */}
                        <div className="flex flex-col">
                            <p className="mb-2 text-xs font-semibold text-color-400 uppercase tracking-widest">
                                {t('birthday & family anniversaries')}
                            </p>
                            <div className="flex flex-col">
                                {profile.date_of_birth && (
                                    <DateRow
                                        icon={<PiUserCircleDashedFill size={14}/>}
                                        iconBg="bg-sky-50"
                                        iconColor="text-sky-500"
                                        label={t('birthday')}
                                        dateStr={profile.date_of_birth}
                                        showAnniversary={true}
                                        isBirthday={true}
                                    />
                                )}
                                {matrimonySacrament?.eff_date && (
                                    <DateRow
                                        icon={<BsHeartFill size={11}/>}
                                        iconBg="bg-rose-50"
                                        iconColor="text-rose-400"
                                        label={t('wedding anniversary')}
                                        dateStr={matrimonySacrament.eff_date}
                                        showAnniversary={true}
                                    />
                                )}
                                {!profile.date_of_birth && !matrimonySacrament && (
                                    <p className="p-2 background-100 rounded-lg text-color-400 italic">{t('no data')}</p>
                                )}
                            </div>
                        </div>
                    </SectionCard>

                    {/* Tài khoản hệ thống */}
                    <SectionCard
                        icon={<BsPersonGear size={15}/>}
                        title={t('system account')}>
                        {profile.system_account ? (
                            <div className="flex flex-col gap-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-primary-50 flex flex-center flex-shrink-0">
                                        <PiUserCircleDashedFill size={18} className="text-primary-400"/>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-sm text-color-000 truncate m-0">
                                            {profile.system_account.email}
                                        </p>
                                    </div>
                                    <span className={classNames(
                                        'text-xs font-semibold px-2 py-1 rounded-full',
                                        profile.system_account.active
                                            ? 'bg-green-100 text-green-600'
                                            : 'bg-color-100 text-color-400'
                                    )}>
                                        {t(profile.system_account.active ? 'active' : 'inactive')}
                                    </span>
                                </div>
                                <p className="mt-3 text-xs text-color-300 italic tracking-wider">
                                    <span className="font-semibold text-gray-600">
                                        {`* ${t('note')}: `}
                                    </span>
                                    {t('this account has access to view information based on assigned roles')}
                                </p>
                            </div>
                        ) : (
                            <p className="p-2 background-100 rounded-lg text-color-400 italic">
                                {t('no system account linked')}
                            </p>
                        )}
                    </SectionCard>
                </div>

                {/* Right: households + departments + contact */}
                <div className="lg:col-span-2 flex flex-col gap-4">
                    {/* Số gia đình CG gắn với org */}
                    <SectionCard
                        icon={<IoHomeOutline size={15}/>}
                        title={t('catholic household book')}>
                        {orgHouseholds.length > 0 ? (
                            <>
                                <p className="text-xs text-color-400 italic mb-3">
                                    {t('this person belongs to {count} family', {count: orgHouseholds.length})}
                                </p>
                                <div className="flex flex-col gap-2">
                                    {orgHouseholds.map((hh, i) => (
                                        <Link
                                            key={hh.id || i}
                                            to={getLinkToDetail(hh)}
                                            className="no-underline">
                                            <div className="p-3 rounded-xl border border-color-50 background-50 hover:background-100 transition-colors cursor-pointer">
                                                {/* Tags row — wrap tự nhiên */}
                                                <div className="flex flex-wrap items-center gap-1.5 mb-2">
                                                    <div className="w-5 h-5 rounded bg-amber-50 flex flex-center flex-shrink-0">
                                                        <IoHomeOutline size={11} className="text-amber-500"/>
                                                    </div>
                                                    {hh.source_idcode && (
                                                        <span className="font-mono text-xs font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                                            {hh.source_idcode}
                                                        </span>
                                                    )}
                                                    {hh.status !== 1 && (
                                                        <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-600">
                                                            {t('pending')}
                                                        </span>
                                                    )}
                                                    {hh.total?.members > 0 && (
                                                        <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-green-50 text-green-600">
                                                            {`${hh.total.members} ${t('members').toLowerCase()}`}
                                                        </span>
                                                    )}
                                                </div>
                                                {/* Title */}
                                                {hh.title && (
                                                    <p className="font-semibold text-sm text-color-000 m-0 leading-snug">
                                                        {hh.title}
                                                    </p>
                                                )}
                                                {/* Parish + date */}
                                                {hh.parish_name && (
                                                    <p className="mt-1 text-xs text-color-400 m-0">
                                                        <GoDotFill size={8} className="inline mr-1"/>
                                                        {hh.parish_name}
                                                    </p>
                                                )}
                                                {hh.eff_date && (
                                                    <p className="mt-0.5 text-xs text-color-300 m-0 italic">
                                                        {`${t('date of issue')}: ${formatDisplayDate(hh.eff_date)}`}
                                                    </p>
                                                )}
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                                <p className="mt-3 text-xs text-color-300 italic tracking-wider">
                                    <span className="font-semibold text-gray-600">
                                        {`* ${t('note')}: `}
                                    </span>
                                    {t('a person can belong to multiple households (parents family and own family after marriage)')}
                                </p>
                            </>
                        ) : (
                            <p className="p-2 background-100 rounded-lg text-color-400 italic">{t('no data')}</p>
                        )}
                    </SectionCard>

                    {/* Nhóm & Đơn vị tham gia */}
                    {profile.department_memberships?.length > 0 && (
                        <SectionCard
                            icon={<PiUsersThree size={15}/>}
                            title={t('groups & units')}>
                            <div className="flex flex-col gap-2 divide-y divide-gray-200">
                                {profile.department_memberships.map((dm, i) => (
                                    <div key={dm.id || i}
                                         className="flex items-center gap-3 border-b border-color-50 last:border-b-0">
                                        <div
                                            className="w-7 h-7 rounded-lg bg-primary-50 flex flex-center flex-shrink-0">
                                            <PiUsersThreeFill size={13} className="text-primary-400"/>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-sm text-color-000 m-0 truncate">
                                                {dm.department_title}
                                            </p>
                                            {dm.group_title && (
                                                <p className="text-xs text-color-400 m-0">{dm.group_title}</p>
                                            )}
                                        </div>
                                        {dm.role_title && (
                                            <span className="text-xs font-semibold text-primary px-2 py-0.5 rounded-full bg-primary-50 flex-shrink-0">
                                                {dm.role_title}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <p className="mt-3 text-xs text-color-300 italic tracking-wider">
                                <span className="font-semibold text-gray-600">
                                    {`* ${t('note')}: `}
                                </span>
                                {t('a member can belong to multiple groups at the same time')}
                            </p>
                        </SectionCard>
                    )}

                    {/* Thông tin liên hệ */}
                    {(profile.contact_phone || profile.contact_email || profile.contact_address) && (
                        <SectionCard
                            icon={<BsPersonVcard size={15}/>}
                            title={t('contact information')}>
                            <div className="flex flex-col gap-2">
                                {renderIf(
                                    profile.contact_phone,
                                    <div className="flex items-center gap-3">
                                        <IoCallOutline size={14} className="text-color-400 flex-shrink-0"/>
                                        <span className="text-sm text-color-100">{profile.contact_phone}</span>
                                    </div>
                                )}
                                {renderIf(
                                    profile.contact_email,
                                    <div className="flex items-center gap-3">
                                        <IoMailOutline size={14} className="text-color-400 flex-shrink-0"/>
                                        <span className="text-sm text-color-100">{profile.contact_email}</span>
                                    </div>
                                )}
                                {renderIf(
                                    profile.contact_address,
                                    <div className="flex items-start gap-3">
                                        <IoLocationOutline size={14} className="text-color-400 flex-shrink-0 mt-0.5"/>
                                        <span className="text-sm text-color-100">{profile.contact_address}</span>
                                    </div>
                                )}
                            </div>
                        </SectionCard>
                    )}
                </div>
            </div>
        </div>
    )
}

export default WorkspaceMemberPersonalProfile
